/*
 * CashierPerformanceTile — admin dashboard rollup of every cashier's
 * daily/weekly numbers. Added 2026-07-26.
 *
 * Now that every self-signup auto-attributes to Winnie, this tile turns
 * that attribution into a single-glance productivity view. Ready to
 * scale when a second cashier is added — rows sort by loads_today.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, TrendingUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtCount = (n) => Number(n || 0).toLocaleString();

const CashierPerformanceTile = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [window, setWindow] = useState('today');  // 'today' | 'week'

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API}/api/admin/cashier-performance`,
                    { withCredentials: true },
                );
                setRows(data.cashiers || []);
            } catch { /* silent — tile self-hides on failure */ }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return null;
    if (rows.length === 0) return null;

    const totals = rows.reduce((acc, r) => ({
        loads: acc.loads + (window === 'today' ? r.loads_today : r.loads_week),
        redeems: acc.redeems + (window === 'today' ? r.redeems_today : r.redeems_week),
        players: acc.players + r.active_players,
    }), { loads: 0, redeems: 0, players: 0 });
    const net = totals.loads - totals.redeems;

    return (
        <Card className="bg-zinc-900/60 border-emerald-500/30" data-testid="cashier-performance-tile">
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-black tracking-wider uppercase">Cashier Performance</span>
                    </div>
                    <div className="flex gap-1 text-[10px]">
                        {['today', 'week'].map((w) => (
                            <button
                                key={w}
                                type="button"
                                onClick={() => setWindow(w)}
                                className={`px-2 py-1 rounded font-black uppercase tracking-widest ${
                                    window === w
                                        ? 'bg-emerald-500 text-zinc-900'
                                        : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                                }`}
                                data-testid={`cashier-perf-window-${w}`}
                            >
                                {w === 'today' ? 'Today' : '7d'}
                            </button>
                        ))}
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {/* Rollup strip */}
                <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                    <div className="rounded-lg bg-black/40 border border-white/5 p-2">
                        <div className="text-[9px] uppercase text-zinc-500 font-bold tracking-widest">Players</div>
                        <div className="text-lg font-black text-white mt-0.5">{fmtCount(totals.players)}</div>
                    </div>
                    <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2">
                        <div className="text-[9px] uppercase text-emerald-300 font-bold tracking-widest">Loads</div>
                        <div className="text-lg font-black text-emerald-300 mt-0.5">{fmt$(totals.loads)}</div>
                    </div>
                    <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2">
                        <div className="text-[9px] uppercase text-amber-300 font-bold tracking-widest">Redeems</div>
                        <div className="text-lg font-black text-amber-300 mt-0.5">{fmt$(totals.redeems)}</div>
                    </div>
                    <div className={`rounded-lg border p-2 ${
                        net >= 0
                            ? 'bg-cyan-500/10 border-cyan-500/30'
                            : 'bg-rose-500/10 border-rose-500/30'
                    }`}>
                        <div className="text-[9px] uppercase font-bold tracking-widest text-white/70">Net</div>
                        <div className={`text-lg font-black mt-0.5 flex items-center justify-center gap-0.5 ${
                            net >= 0 ? 'text-cyan-300' : 'text-rose-300'
                        }`}>
                            {net >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                            {fmt$(Math.abs(net))}
                        </div>
                    </div>
                </div>

                {/* Per-cashier rows */}
                <div className="space-y-1.5">
                    {rows.map((r) => {
                        const loads = window === 'today' ? r.loads_today : r.loads_week;
                        const redeems = window === 'today' ? r.redeems_today : r.redeems_week;
                        const cnet = loads - redeems;
                        return (
                            <div
                                key={r.id}
                                className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/5 px-2.5 py-1.5"
                                data-testid={`cashier-perf-row-${r.id}`}
                            >
                                <div className="w-7 h-7 shrink-0 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-xs font-black">
                                    {(r.name || '?').slice(0, 1).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-black text-white truncate">{r.name}</div>
                                    <div className="text-[10px] text-zinc-500">
                                        {fmtCount(r.active_players)} player{r.active_players === 1 ? '' : 's'}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[10px] font-mono text-emerald-300">+{fmt$(loads)}</div>
                                    <div className="text-[10px] font-mono text-amber-300">−{fmt$(redeems)}</div>
                                </div>
                                <div className={`text-sm font-black tabular-nums text-right w-20 ${
                                    cnet >= 0 ? 'text-cyan-300' : 'text-rose-300'
                                }`}>
                                    {cnet >= 0 ? '+' : '−'}{fmt$(Math.abs(cnet))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <p className="text-[9px] text-zinc-600 mt-2 flex items-center gap-1">
                    <TrendingUp className="w-2.5 h-2.5" />
                    Central Time · sorted by {window === 'today' ? "today's" : '7-day'} loads
                </p>
            </CardContent>
        </Card>
    );
};

export default CashierPerformanceTile;
